/**
 * iter 264d.17 — Generic whitelist-preference merger.
 *
 * A whitelist-shaped preference is one where the user stores an ordered
 * subset of a known universe (status order, column order, etc.). When new
 * entries are added to the app after the user saved their preference, the
 * legacy code would return -1 for `indexOf` on the missing entries and
 * push them to the bottom (the Mainline "On the way to Return" isolated
 * teal row bug from iter 264d.16).
 *
 * This helper preserves the user's saved relative order AND slots every
 * missing default entry at its default index (clamped). Same semantics as
 * backend `merge_prefs_with_defaults` in routes/preferences.py.
 *
 * Also mirrored by `mergeStatusOrderWithDefault` in shipmentDashboardHelpers.js
 * for the Imports dashboard's `status_order`; that helper is now a thin
 * alias for `mergeWhitelistWithDefault(saved, default)`.
 */

export const mergeWhitelistWithDefault = (savedOrder, defaultOrder) => {
  const saved = Array.isArray(savedOrder) ? savedOrder.filter(Boolean) : [];
  const dflt = Array.isArray(defaultOrder) ? defaultOrder : [];
  if (saved.length === 0) return [...dflt];
  const seen = new Set(saved);
  const merged = [...saved];
  dflt.forEach((item, idx) => {
    if (seen.has(item)) return;
    const insertAt = Math.min(idx, merged.length);
    merged.splice(insertAt, 0, item);
    seen.add(item);
  });
  return merged;
};

/**
 * iter 264d.18 — Column-preference merger. Same pattern as
 * mergeWhitelistWithDefault but for objects keyed by `id`.
 *
 *   • Preserves the user's saved column ORDER for every column they know
 *     about (drag-drop rearrangement is respected).
 *   • Adds any NEW column from `defaultColumns` that wasn't in the saved
 *     list at its DEFAULT position.
 *   • For every kept column, merges the saved object's fields ON TOP of
 *     the default (so widths / visibility / label overrides survive) —
 *     with a floor of `default.width` if saved.width is missing.
 *   • Protected columns (marker, actions) always sit at their default
 *     positions in the returned array (marker prepended, actions appended).
 *
 * @param {Array<object>} savedColumns - user's saved column list
 * @param {Array<object>} defaultColumns - source-of-truth column list
 * @param {Array<string>} protectedIds - column ids that must sit at ends
 */
export const mergeColumnsWithDefault = (
  savedColumns,
  defaultColumns,
  protectedIds = ['marker', 'actions'],
) => {
  const saved = Array.isArray(savedColumns) ? savedColumns.filter((c) => c && c.id) : [];
  const defaults = Array.isArray(defaultColumns) ? defaultColumns : [];
  if (saved.length === 0) return [...defaults];

  const defaultById = new Map(defaults.map((c) => [c.id, c]));
  const savedById = new Map(saved.map((c) => [c.id, c]));

  // Preserve saved order for anything the default still knows about.
  const ordered = [];
  saved.forEach((s) => {
    if (!defaultById.has(s.id)) return;
    const def = defaultById.get(s.id);
    ordered.push({ ...def, ...s, width: s.width || def.width });
  });

  // Append any new-in-defaults columns at their DEFAULT index (clamped).
  defaults.forEach((defCol, idx) => {
    if (savedById.has(defCol.id)) return;
    const insertAt = Math.min(idx, ordered.length);
    ordered.splice(insertAt, 0, defCol);
  });

  // Marker column always at index 0; actions always at the end.
  const isProtected = (id) => protectedIds.includes(id);
  const nonProtected = ordered.filter((c) => !isProtected(c.id));
  const marker = ordered.find((c) => c.id === 'marker') || defaults.find((c) => c.id === 'marker');
  const actions = ordered.find((c) => c.id === 'actions') || defaults.find((c) => c.id === 'actions');
  const final = [];
  if (marker) final.push(marker);
  final.push(...nonProtected);
  if (actions) final.push(actions);
  return final;
};

/**
 * Apply the merger to every known whitelist key on a preferences object.
 * Non-whitelist fields pass through unchanged. Idempotent.
 *
 * @param {object} prefs — user_preferences doc from GET /user/preferences
 * @param {object} defaults — { key: [defaultOrder] } map
 * @returns {object} shallow-cloned prefs with whitelists merged
 */
export const mergePrefsWithDefaults = (prefs, defaults) => {
  if (!prefs || typeof prefs !== 'object') return prefs;
  const out = { ...prefs };
  Object.entries(defaults || {}).forEach(([key, defaultList]) => {
    if (key in out && Array.isArray(out[key])) {
      out[key] = mergeWhitelistWithDefault(out[key], defaultList);
    }
  });
  return out;
};
